import React, { useState } from 'react';
import { STORE_CATALOG, getLevelInfo, getAvailableXP } from '../utils';

const CATS = [
  { id: 'themes',  label: '🎨 Themes' },
  { id: 'accents', label: '✨ Accents' },
  { id: 'titles',  label: '🏷️ Titles' },
  { id: 'effects', label: '🎊 Effects' },
];

export default function XPStore({ state, updateState, showToast, setTheme, currentTheme = 'dark', onClose }) {
  const [cat, setCat] = useState('themes');
  const levelInfo = getLevelInfo(state);
  const availableXP = getAvailableXP(state);
  const items = STORE_CATALOG.filter(i => i.cat === cat);
  const owned = state.storeOwned || [];
  const equipped = state.storeEquipped || {};

  const isUnlocked = (item) => {
    if (levelInfo.level < item.levelReq) return false;
    if (item.xpCost === 0) return true;
    return owned.includes(item.id);
  };

  const isEquipped = (item) => {
    if (item.type === 'theme') return currentTheme === item.value;
    if (item.type === 'accent') return (equipped.accentColor || 'accent_gold') === item.id;
    if (item.type === 'title') return equipped.profileTitle === item.id;
    if (item.type === 'confetti') return (equipped.confettiStyle || 'fx_classic') === item.id;
    return false;
  };

  const handlePurchase = (item) => {
    if (availableXP < item.xpCost) { showToast('Not enough XP!'); return; }
    updateState({
      storeOwned: [...owned, item.id],
      xpSpent: (state.xpSpent || 0) + item.xpCost,
    });
    showToast(`${item.em} Unlocked ${item.name}!`);
  };

  const handleEquip = (item) => {
    if (item.type === 'theme') {
      setTheme(item.value);
      showToast(`${item.em} ${item.name} theme equipped!`);
    } else if (item.type === 'accent') {
      updateState({ storeEquipped: { ...equipped, accentColor: item.id } });
      showToast(`${item.em} ${item.name} accent equipped!`);
    } else if (item.type === 'title') {
      const newTitle = equipped.profileTitle === item.id ? null : item.id;
      updateState({ storeEquipped: { ...equipped, profileTitle: newTitle } });
      showToast(newTitle ? `${item.em} "${item.value}" title equipped!` : 'Title removed');
    } else if (item.type === 'confetti') {
      updateState({ storeEquipped: { ...equipped, confettiStyle: item.id } });
      showToast(`${item.em} ${item.name} effect equipped!`);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--bdr)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>🛍️ XP Store</div>
              <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 2 }}>Unlock cosmetics with XP as you level up</div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: '1px solid var(--bdr)', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', color: 'var(--mut)', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >✕</button>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1, padding: '10px 14px', background: 'var(--surf2)', borderRadius: 10, border: '1px solid var(--bdr)' }}>
              <div style={{ fontSize: 10, color: 'var(--mut)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Available XP</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--ylw)' }}>{availableXP.toLocaleString()}</div>
            </div>
            <div style={{ flex: 1, padding: '10px 14px', background: 'var(--surf2)', borderRadius: 10, border: '1px solid var(--bdr)' }}>
              <div style={{ fontSize: 10, color: 'var(--mut)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Level</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--ylw)' }}>
                {levelInfo.level} <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--mut)' }}>{levelInfo.name}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Category tabs */}
        <div style={{ display: 'flex', padding: '8px 12px 0', gap: 4 }}>
          {CATS.map(c => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              style={{
                flex: 1, padding: '8px 4px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                border: 'none', borderBottom: cat === c.id ? '2px solid var(--ylw)' : '2px solid transparent',
                borderRadius: 0, background: 'transparent',
                color: cat === c.id ? 'var(--ylw)' : 'var(--mut)',
              }}
            >{c.label}</button>
          ))}
        </div>

        {/* Items grid */}
        <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {items.map(item => {
              const unlocked = isUnlocked(item);
              const eq = isEquipped(item);
              const canBuy = levelInfo.level >= item.levelReq && item.xpCost > 0 && !owned.includes(item.id);
              const locked = levelInfo.level < item.levelReq;

              return (
                <div key={item.id} style={{
                  padding: '14px 12px', background: 'var(--surf2)',
                  border: `1px solid ${eq ? 'var(--ylw)' : 'var(--bdr)'}`,
                  borderRadius: 12, opacity: locked ? 0.55 : 1, position: 'relative',
                }}>
                  {eq && (
                    <div style={{ position: 'absolute', top: 8, right: 8, fontSize: 9, fontWeight: 800, background: 'var(--ylw)', color: '#000', padding: '2px 6px', borderRadius: 99, textTransform: 'uppercase' }}>
                      ON
                    </div>
                  )}
                  <div style={{ fontSize: 28, marginBottom: 6 }}>{item.em}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 2, color: 'var(--txt)' }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 8, lineHeight: 1.4 }}>{item.desc}</div>

                  {item.type === 'accent' && (
                    <div style={{ width: '100%', height: 4, borderRadius: 99, background: item.value, marginBottom: 8 }} />
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: locked ? 'var(--red)' : 'var(--grn)' }}>
                      {locked ? `🔒 Lv ${item.levelReq}` : `✓ Lv ${item.levelReq}`}
                    </span>
                    {item.xpCost > 0 && (
                      <span style={{ fontSize: 10, color: 'var(--ylw)', fontWeight: 700, marginLeft: 'auto' }}>
                        {owned.includes(item.id) ? '✓ Owned' : `${item.xpCost.toLocaleString()} XP`}
                      </span>
                    )}
                  </div>

                  {locked ? (
                    <div style={{ fontSize: 11, color: 'var(--mut)', textAlign: 'center', padding: '6px 0', fontStyle: 'italic' }}>
                      {item.levelReq - levelInfo.level} level{item.levelReq - levelInfo.level !== 1 ? 's' : ''} away
                    </div>
                  ) : canBuy ? (
                    <button
                      onClick={() => handlePurchase(item)}
                      disabled={availableXP < item.xpCost}
                      style={{
                        width: '100%', padding: '7px', borderRadius: 8, fontWeight: 800, fontSize: 12, cursor: availableXP >= item.xpCost ? 'pointer' : 'not-allowed',
                        background: availableXP >= item.xpCost ? 'var(--ylw)' : 'var(--surf)',
                        color: availableXP >= item.xpCost ? '#000' : 'var(--mut)',
                        border: `1px solid ${availableXP >= item.xpCost ? 'transparent' : 'var(--bdr)'}`,
                      }}
                    >
                      Buy — {item.xpCost.toLocaleString()} XP
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEquip(item)}
                      style={{
                        width: '100%', padding: '7px', borderRadius: 8, fontWeight: 800, fontSize: 12, cursor: 'pointer',
                        background: eq ? 'rgba(255,217,61,.12)' : 'var(--surf)',
                        color: eq ? 'var(--ylw)' : 'var(--txt)',
                        border: `1px solid ${eq ? 'var(--ylw)' : 'var(--bdr)'}`,
                      }}
                    >
                      {eq ? (item.type === 'title' ? 'Remove title' : 'Equipped ✓') : 'Equip'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--bdr)', fontSize: 11, color: 'var(--mut)', textAlign: 'center' }}>
          Earn XP by staying under budget, hitting milestones, and logging daily ⚡
        </div>
      </div>
    </div>
  );
}
